<!-- Shared template for header content -->
<ng-template #headerTopContent let-isSticky="isSticky">
  <div class="header__nav">
    <!-- Logo: always links to homepage, shows back arrow on hover in article mode -->
    <a
      class="header__logo header__logo--clickable"
      [class.header__logo--with-back]="mode() === 'article'"
      routerLink="/"
      puTestId="header.logo"
    >
      <img
        src="assets/tellent-updates-icon.png"
        alt="Tellent Product Updates"
        class="header__logo-img"
      />
      <rt-icon name="keyboard-arrow-left" class="header__logo-back-icon" />
    </a>

    <!-- Mobile: show active tab label next to logo -->
    @if (mode() === 'feed') {
      <span class="header__active-tab-label">
        {{ getActiveMainTabLabel() }}
        @if (activeMainTab() === 'timeline' && stateService.timelineQuarterLabel()) {
          <rt-icon name="keyboard-arrow-right" class="header__breadcrumb-chevron" />
          <span class="header__breadcrumb-item">{{ stateService.timelineQuarterLabel() }}</span>
          <rt-icon name="keyboard-arrow-right" class="header__breadcrumb-chevron" />
          <span class="header__breadcrumb-item header__breadcrumb-item--current">{{
            stateService.timelineMonthLabel()
          }}</span>
        }
      </span>
    }

    @if (mode() === 'article') {
      <!-- Article mode (desktop): title in nav position (same styling as active nav item) -->
      @if (primaryTranslation()) {
        <span class="header__article-title-nav" [title]="primaryTranslation()!.title">
          {{ primaryTranslation()!.title }}
        </span>
      } @else {
        <!-- Loading skeleton for title -->
        <rt-placeloader class="header__title-skeleton-nav" variant="text" />
      }

      <!-- Article mode (mobile): badges inline with logo -->
      @if (article()) {
        <div class="header__article-badges-mobile">
          @for (badge of badges(); track badge.category) {
            <pu-category-badge [category]="badge.category" [type]="badge.type" variant="ghost" />
          }
        </div>
      } @else {
        <div class="header__article-badges-mobile header__article-badges-mobile--loading">
          <rt-placeloader class="header__badge-skeleton" variant="text" [short]="true" />
          <rt-placeloader class="header__badge-skeleton" variant="text" [short]="true" />
        </div>
      }
    } @else {
      <!-- Feed mode: show navigation tabs -->
      <nav class="header__main-nav" puTestId="header.nav">
        <a
          class="header__main-nav-item"
          [class.header__main-nav-item--active]="activeMainTab() === 'whats-new'"
          [attr.data-text]="'nav.whats_new' | translate"
          (click)="onMainTabChange('whats-new')"
          puTestId="header.nav.whats-new"
        >
          {{ 'nav.whats_new' | translate }}
        </a>
        <a
          class="header__main-nav-item"
          [class.header__main-nav-item--active]="activeMainTab() === 'coming-soon'"
          [attr.data-text]="'nav.coming_soon' | translate"
          (click)="onMainTabChange('coming-soon')"
          puTestId="header.nav.coming-soon"
        >
          {{ 'nav.coming_soon' | translate }}
        </a>
        <a
          class="header__main-nav-item"
          [class.header__main-nav-item--active]="activeMainTab() === 'timeline'"
          [attr.data-text]="'nav.timeline' | translate"
          (click)="onMainTabChange('timeline')"
          puTestId="header.nav.timeline"
        >
          {{ 'nav.timeline' | translate }}
        </a>
        @if (stateService.hasChangelogPosts()) {
          <a
            class="header__main-nav-item"
            [class.header__main-nav-item--active]="activeMainTab() === 'changelog'"
            [attr.data-text]="'nav.changelog' | translate"
            (click)="onMainTabChange('changelog')"
            puTestId="header.nav.changelog"
          >
            {{ 'nav.changelog' | translate }}
          </a>
        }
      </nav>
    }
  </div>

  <div class="header__actions">
    <!-- Hamburger menu button (mobile only) -->
    <button
      class="header__hamburger"
      (click)="toggleMobileMenu()"
      [attr.aria-expanded]="isMobileMenuOpen()"
      [attr.aria-label]="'accessibility.open_navigation_menu' | translate"
      puTestId="header.hamburger"
    >
      <rt-icon name="tellent-nav-hamburger-inactive" />
    </button>

    <rt-icon-button
      size="small"
      variant="plain-ghost"
      [iconName]="isDarkMode() ? 'dark' : 'light'"
      (click)="toggleTheme()"
      [attr.aria-label]="'accessibility.toggle_theme' | translate"
      puTestId="header.theme-toggle"
    />

    <div class="header__language-dropdown" puTestId="header.language.dropdown">
      <rt-button
        variant="plain-ghost"
        size="small"
        class="header__language-button"
        [class.header__language-button--open]="isLanguageDropdownOpen()"
        (click)="toggleLanguageDropdown()"
        puTestId="header.language.button"
      >
        {{ getLanguageLabel(stateService.currentLocale()) }}
        <rt-icon name="keyboard-arrow-down" class="header__language-chevron" />
      </rt-button>

      @if (isLanguageDropdownOpen()) {
        <div class="header__language-menu" puTestId="header.language.menu">
          @for (locale of stateService.availableLocales(); track locale) {
            <button
              class="header__language-option"
              [class.header__language-option--active]="locale === stateService.currentLocale()"
              (click)="selectLanguage(locale)"
              puTestId="header.language.option"
              [testData]="{ locale: locale }"
            >
              {{ getLanguageLabel(locale) }}
            </button>
          }
        </div>
      }
    </div>
  </div>
</ng-template>

<!-- Normal header (always in document flow) -->
<header class="header" [class.header--article]="mode() === 'article'" puTestId="header">
  <div class="header__glass">
    <div class="header__inner">
      <div class="header__top">
        <ng-container *ngTemplateOutlet="headerTopContent; context: { isSticky: false }" />
      </div>

      @if (mode() === 'article') {
        <!-- Article mode row 2: badges on desktop, title on mobile -->
        <div class="header__article-row2">
          <!-- Desktop: badges -->
          @if (article()) {
            <div class="header__article-badges">
              @for (badge of badges(); track badge.category) {
                <pu-category-badge
                  [category]="badge.category"
                  [type]="badge.type"
                  variant="ghost"
                />
              }
            </div>
          } @else {
            <!-- Loading skeleton for badges -->
            <div class="header__article-badges header__article-badges--loading">
              <rt-placeloader class="header__badge-skeleton" variant="text" [short]="true" />
              <rt-placeloader class="header__badge-skeleton" variant="text" [short]="true" />
            </div>
          }

          <!-- Mobile: title -->
          @if (primaryTranslation()) {
            <h1 class="header__article-title-mobile">
              {{ primaryTranslation()!.title }}
            </h1>
          } @else {
            <rt-placeloader class="header__title-skeleton-mobile" variant="text" />
          }
        </div>
      }

      @if (mode() === 'feed') {
        <!-- Feed mode: show product tabs -->
        <div class="header__product-tabs-wrapper">
          <nav class="header__product-tabs" puTestId="header.product-tabs">
            @for (tab of productTabs; track tab) {
              <button
                class="header__product-tab"
                [class.header__product-tab--active]="activeProductTab() === tab"
                (click)="onProductTabChange(tab)"
                puTestId="header.product-tab"
                [testData]="{ product: tab }"
              >
                <span class="header__product-tab-label">{{ getProductTabLabel(tab) }}</span>
                <div class="header__product-tab-indicator"></div>
              </button>
              <!-- Dot separator to group Hire/Manage/Grow -->
              @if (tab === 'ai') {
                <span class="header__product-tab-separator">·</span>
              }
            }

            <a
              class="header__feedback"
              href="https://feedback.tellent.com"
              target="_blank"
              rel="noopener noreferrer"
              puTestId="header.feedback-link"
            >
              <rt-button variant="plain-ghost" size="small">
                {{ 'header.give_feedback' | translate }}
              </rt-button>
            </a>
          </nav>
        </div>
      }
    </div>
  </div>
</header>

<!-- Sticky header (fixed, animated) - only for feed mode -->
@if (mode() === 'feed') {
  <header
    class="header header--sticky"
    [ngClass]="{
      'header--sticky-visible': stickyState() === 'visible',
      'header--sticky-hiding': stickyState() === 'hiding',
    }"
  >
    <div class="header__glass">
      <div class="header__inner">
        <div class="header__top">
          <ng-container *ngTemplateOutlet="headerTopContent; context: { isSticky: true }" />
        </div>
      </div>
    </div>
  </header>
}

<!-- Mobile navigation menu -->
@if (isMobileMenuOpen()) {
  <pu-mobile-menu
    [isOpen]="isMobileMenuOpen()"
    [activeMainTab]="activeMainTab()"
    [activeProductTab]="activeProductTab()"
    [currentLocale]="stateService.currentLocale()"
    [availableLocales]="stateService.availableLocales()"
    [isDarkMode]="isDarkMode()"
    (closeMenu)="closeMobileMenu()"
    (mainTabChange)="onMobileMainTabChange($event)"
    (productTabChange)="onMobileProductTabChange($event)"
    (localeChange)="selectLanguage($event)"
    (themeToggle)="toggleTheme()"
  />
}
